import React, { useState, useEffect, useCallback } from 'react';
import campaignArchitectService, {
  ChannelMix,
  AudienceProfile,
  SequenceArchitecture,
} from '../services/campaignArchitectService';
import gtmService, { Icp, Persona, Offer } from '../services/gtmService';
import MarkdownView from '../components/MarkdownView';
import SavedAiDocuments from '../components/SavedAiDocuments';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const CHANNEL_OPTIONS: { value: ChannelMix; label: string }[] = [
  { value: 'linkedin_first', label: 'LinkedIn-first' },
  { value: 'linkedin_email', label: 'LinkedIn + E-Mail' },
  { value: 'email_first', label: 'E-Mail-first' },
  { value: 'email', label: 'Nur E-Mail' },
  { value: 'linkedin_email_call', label: 'LinkedIn + E-Mail + Call' },
];

const AUDIENCE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '— Profil (optional) —' },
  { value: 'young_urban', label: 'Jung / urban / Tech (<35)' },
  { value: 'traditional', label: 'Traditionell / Corporate (40+)' },
];

const channelColor = (ch: string): string =>
  /linkedin/i.test(ch) ? '#0a66c2' : /call|anruf|phone/i.test(ch) ? '#7c3aed' : 'var(--sec-success)';

const CampaignArchitectPage: React.FC = () => {
  const [channelMix, setChannelMix] = useState<ChannelMix>('linkedin_email');
  const [listSize, setListSize] = useState('');
  const [audienceProfile, setAudienceProfile] = useState('');
  const [goal, setGoal] = useState('');
  const [context, setContext] = useState('');
  const [icpId, setIcpId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [offerId, setOfferId] = useState('');

  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [arch, setArch] = useState<SequenceArchitecture | null>(null);
  const [raw, setRaw] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const loadGtm = useCallback(async () => {
    try {
      const [i, p, o] = await Promise.all([
        gtmService.getIcps(),
        gtmService.getPersonas(),
        gtmService.getOffers(),
      ]);
      setIcps(i);
      setPersonas(p);
      setOffers(o);
    } catch {
      // optional
    }
  }, []);

  useEffect(() => {
    loadGtm();
  }, [loadGtm]);

  const handleDesign = async () => {
    try {
      setLoading(true);
      setError('');
      setArch(null);
      setRaw('');
      const result = await campaignArchitectService.design({
        channelMix,
        listSize: listSize ? Number(listSize) : undefined,
        audienceProfile: (audienceProfile || undefined) as AudienceProfile | undefined,
        goal: goal || undefined,
        context: context || undefined,
        icpId: icpId || undefined,
        personaId: personaId || undefined,
        offerId: offerId || undefined,
      });
      if (result.architecture) setArch(result.architecture);
      else setRaw(result.raw || 'Keine strukturierte Architektur erhalten.');
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Entwurf der Sequenz');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <PageHeader title="Kampagnen-Architekt" subtitle={routeById('campaign-architect')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Entwirft die optimale mehrstufige Outbound-<strong>Sequenz</strong> (Cadence) <em>bevor</em>
        Copy geschrieben wird — datenbasiert auf 244K+ lemlist-Kampagnen (LinkedIn-first, 3-Schritt-
        Sweet-Spot, enge Listen). Powered by Skill <em>outbound-campaign-architect</em>.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Kanalmix</label>
            <select className="form-input" value={channelMix} onChange={(e) => setChannelMix(e.target.value as ChannelMix)}>
              {CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 140px' }}>
            <label className="form-label">Listengröße</label>
            <input type="number" className="form-input" value={listSize} onChange={(e) => setListSize(e.target.value)} placeholder="z.B. 40" />
          </div>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Zielgruppen-Profil</label>
            <select className="form-input" value={audienceProfile} onChange={(e) => setAudienceProfile(e.target.value)}>
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">ICP (GTM)</label>
            <select className="form-input" value={icpId} onChange={(e) => setIcpId(e.target.value)}>
              <option value="">— keiner —</option>
              {icps.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">Persona (GTM)</label>
            <select className="form-input" value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">— keine —</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 180px' }}>
            <label className="form-label">Angebot (GTM)</label>
            <select className="form-input" value={offerId} onChange={(e) => setOfferId(e.target.value)}>
              <option value="">— keins —</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Ziel / CTA</label>
          <input className="form-input" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="z.B. Discovery-Call buchen" />
        </div>
        <div className="form-group">
          <label className="form-label">Kontext (optional)</label>
          <input className="form-input" value={context} onChange={(e) => setContext(e.target.value)} placeholder="cold / post-trigger / warm + Freitext" />
        </div>

        <button className="btn btn-success" onClick={handleDesign} disabled={loading}>
          {loading ? 'Entwerfe Sequenz…' : 'Sequenz entwerfen'}
        </button>
      </div>

      {raw && <MarkdownView content={raw} style={{ marginTop: '16px' }} />}

      {arch && (
        <div style={{ marginTop: '20px' }}>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="card-title">🎯 {arch.campaignName}</div>
            <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '8px' }}>
              {arch.icp && <div><strong>ICP:</strong> {arch.icp}</div>}
              {arch.offer && <div><strong>Offer:</strong> {arch.offer}</div>}
              {arch.recommendedListSize && <div><strong>Empfohlene Listengröße:</strong> {arch.recommendedListSize}</div>}
              {arch.expectedReplyRate && <div><strong>Erwartete Reply-Rate:</strong> {arch.expectedReplyRate}</div>}
            </div>
          </div>

          {arch.steps.map((s) => (
            <div className="card" key={s.step} style={{ borderLeft: `4px solid ${channelColor(s.channel)}`, marginBottom: '10px' }}>
              <div className="card-title">
                Schritt {s.step} · Tag {s.day} · {s.channel} — {s.action}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '6px' }}>
                {s.purpose && <div><strong>Zweck:</strong> {s.purpose}</div>}
                {s.conditionalLogic && <div><strong>Bedingung:</strong> {s.conditionalLogic}</div>}
                {s.timingNote && <div><strong>Timing:</strong> {s.timingNote}</div>}
              </div>
            </div>
          ))}

          {arch.branches?.length > 0 && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '6px' }}>Bedingte Verzweigungen</div>
              <ul style={{ margin: '0 0 0 18px', fontSize: '13px', color: 'var(--sec-fg)' }}>
                {arch.branches.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}

          {arch.rationale && (
            <div className="alert alert-success" style={{ marginBottom: '12px' }}>
              <strong>Warum diese Architektur:</strong> {arch.rationale}
            </div>
          )}

          {arch.avoid?.length > 0 && (
            <div className="alert alert-error" style={{ marginBottom: '12px' }}>
              <strong>Nicht tun:</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>
                {arch.avoid.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <p style={{ fontSize: '13px', color: 'var(--sec-fg-muted)' }}>
            Nächster Schritt: Texte je Schritt unter <strong>Kaltakquise-Texte (Cold-Copy Studio)</strong> schreiben.
          </p>
        </div>
      )}

      <SavedAiDocuments feature="campaign_architect" reloadKey={reloadKey} />
    </div>
  );
};

export default CampaignArchitectPage;
